import {Sound, filters} from '@pixi/sound';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {ISoundTool, SoundProperties, SoundsProperties} from '@gongxigames/framework/slot/types';

export class SoundTool implements ISoundTool {
    protected soundManager: SoundManager;

    constructor(soundManager: SoundManager) {
        this.soundManager = soundManager;
    }

    public editSound(source: string, property: string, value: any): void {
        const sound = this.getSoundByFileName(source);
        const soundName = this.soundManager.audioNamesBySource[source];

        if (!this.soundManager.soundsProperties[soundName]) {
            this.soundManager.soundsProperties[soundName] = {};
        }

        const config = this.soundManager.soundsProperties[soundName];

        if (sound && property === 'volume') {
            config.volume = value;
            sound.volume = value;
        } else if (sound && property === 'panning') {
            config.panning = value;
            sound.filters = [new filters.StereoFilter(value)];
        } else {
            throw new Error(`Editing of this (${property}) parameter is not implemented!`);
        }
    }

    public playSound(source: string): void {
        const sound = this.getSoundByFileName(source);

        if (sound) {
            sound.play();
        }
    }

    public replaceSounds(sounds: Map<string, Sound>): void {
        for (const [fileName, sound] of sounds.entries()) {
            let oldSound;
            let newSound;

            if (this.soundManager.hasSoundBySource(fileName)) {
                oldSound = this.soundManager.getSoundBySource(fileName);

                this.soundManager.setSoundBySource(fileName, sound);

                newSound = this.soundManager.getSoundBySource(fileName);
            } else if (this.soundManager.hasMusicBySource(fileName)) {
                oldSound = this.soundManager.getMusicBySource(fileName);

                this.soundManager.setMusicBySource(fileName, sound);

                newSound = this.soundManager.getMusicBySource(fileName);
            }

            if (oldSound?.isPlaying) {
                oldSound.stop();

                if (newSound) {
                    newSound.volume = oldSound.volume;
                    newSound.loop = oldSound.loop;
                    newSound.filters = oldSound.filters;
                    newSound.play();
                }
            }
        }
    }

    public stopAllSounds(): void {
        const sounds = this.soundManager.getSoundKeys();
        const music = this.soundManager.getMusicKeys();

        sounds.forEach(soundId => this.soundManager.getSound(soundId)?.stop());
        music.forEach(musicId => this.soundManager.getMusic(musicId)?.stop());
    }

    public stopSound(source: string): void {
        const sound = this.getSoundByFileName(source);

        if (sound) {
            sound.stop();
        }
    }

    public get sounds(): Map<string, Sound> {
        const sounds: Map<string, Sound> = new Map([]);
        const soundIds = this.soundManager.getSoundKeys();
        const musicIds = this.soundManager.getMusicKeys();

        soundIds.forEach(id => sounds.set(id, this.soundManager.getSound(id)));
        musicIds.forEach(id => sounds.set(id, this.soundManager.getMusic(id)));

        return sounds;
    }

    public getSound(id: string): Sound | undefined {
        if (this.soundManager.hasSound(id)) {
            return this.soundManager.getSound(id);
        } else if (this.soundManager.hasMusic(id)) {
            return this.soundManager.getMusic(id);
        }
    }

    public getSoundByFileName(source: string): Sound | undefined {
        if (this.soundManager.hasSoundBySource(source)) {
            return this.soundManager.getSoundBySource(source);
        } else if (this.soundManager.hasMusicBySource(source)) {
            return this.soundManager.getMusicBySource(source);
        }
    }

    public getSoundConfig(name: string): SoundProperties {
        return this.soundsProperties[name] || {};
    }

    public get soundsProperties(): SoundsProperties {
        return this.soundManager.soundsProperties;
    }
}
